import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { formatTextEllipsis, formatValueWithCompactNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ConnectWalletModal, useBoomWallet } from '@boomeme/wallet-sdk';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CopyNode from '@/widget/CopyNode';
import AccountAvatar from '@/widget/AccountAvatar';
import { BscIcon, Deposit, Disconnect, GraySol, Withdraw } from '@/widget/Icon';
import { useTheme } from '@/components/ThemeProvider';
import { useNavigate } from 'react-router-dom';
import { Gift } from 'lucide-react';
import { useAppEventBusContext } from '@/context/eventBusContext';
import { SubscribeEvent } from '@/interface/event';
import { useAppStore } from '@/store';

export default () => {
  const { fwClass, chainCoinBalance, chainInfo, setProfile, setDepositOpen, setWithdrawOpen } = useAppStore();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { isConnected, walletAddress, disconnect } = useBoomWallet();

  const { subscribe } = useAppEventBusContext();

  useEffect(() => {
    const unsubscribe = subscribe(SubscribeEvent.Open_Wallet_Modal, setIsOpen);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isConnected) {
      setProfile({} as any);
    }
  }, [isConnected]);

  useEffect(() => {
    localStorage.setItem('currentWalletAddress', walletAddress || '');
  }, [walletAddress]);

  return (
    <>
      {isConnected && walletAddress ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className="flex items-center gap-x-[7px] text-[12px]">
              <AccountAvatar walletAddress={walletAddress} className="size-[36px]" />
              <div>
                <div className="flex items-center gap-x-[5px]">
                  {chainInfo.chain == 'solana' ? (
                    <GraySol theme={theme} className="size-[12px]" />
                  ) : (
                    <BscIcon theme={theme} className="size-[12px]" />
                  )}
                  <span>{formatValueWithCompactNumber(chainCoinBalance)}</span>
                </div>
                <p className="text-secondaryTextColor underline underline-offset-2">{formatTextEllipsis(walletAddress, 4, 4)}</p>
              </div>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[180px]">
            <DropdownMenuGroup>
              <DropdownMenuSub>
                <DropdownMenuItem className="flex items-center">
                  <Button className="rounded-[8px] w-[16px] h-[16px] relative" variant="ghost" size="icon">
                    <img src="/icon/green.user.svg" className="w-[16px] h-[16px] flex-shrink-0" />
                  </Button>
                  <span className={cn('text-[14px]', fwClass)}>{formatTextEllipsis(walletAddress, 4, 4)}</span>
                  <CopyNode text={walletAddress || ''} />
                </DropdownMenuItem>
              </DropdownMenuSub>
              <DropdownMenuSub>
                <DropdownMenuItem>
                  <Button className="rounded-[8px] w-[16px] h-[16px] relative" variant="ghost" size="icon">
                    <img src={chainInfo.icon} className="size-[16px] flex-shrink-0" />
                  </Button>
                  <span className={cn('text-[14px]', fwClass)}>
                    {formatValueWithCompactNumber(chainCoinBalance)} {chainInfo.symbol}
                  </span>
                </DropdownMenuItem>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate(`/${chainInfo.path}/referral`)}>
                <Button className="rounded-[8px] w-[16px] h-[16px] relative" variant="ghost" size="icon">
                  <Gift />
                </Button>
                <span>{t('Header.referral')}</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDepositOpen(true)}>
                <Button className="rounded-[8px] w-[16px] h-[16px] relative" variant="ghost" size="icon">
                  <Deposit theme={theme} className="w-[16px] h-[16px]" />
                </Button>
                <div>{t('Header.deposit')}</div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setWithdrawOpen(true)}>
                <Button className="rounded-[8px] w-[16px] h-[16px] relative" variant="ghost" size="icon">
                  <Withdraw theme={theme} className="w-[16px] h-[16px]" />
                </Button>
                <div>{t('Header.withdraw')}</div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  disconnect?.();
                }}
              >
                <Button className="rounded-[8px] w-[16px] h-[16px] relative" variant="ghost" size="icon">
                  <Disconnect theme={theme} className="w-[16px] h-[16px]" />
                </Button>
                <div>{t('Header.logout')}</div>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <Button
          className="rounded-[8px] h-[36px] w-[36px] bg-themeColor dark:bg-themeColor dark:hover:bg-themeColor focus-visible:bg-themeColor focus:bg-themeColor"
          variant="outline"
          size="icon"
          onClick={() => setIsOpen(!isOpen)}
        >
          <img src="/icon/single.wallet.svg" className="w-[20px] h-[20px] flex-shrink-0" />
        </Button>
      )}
      <ConnectWalletModal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
      />
    </>
  );
};
